import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatInTimeZone } from "date-fns-tz";
import { Button, OutlineButton } from "components/button";
import { useAuthContext } from "contexts/auth";
import { convertToUTC } from "utils/utcConversion";

interface ScheduleModalProps {
  isOpen: boolean;
  campaignName: string;
  recipientCount: number | null;
  isScheduling: boolean;
  onConfirm: (scheduledAt: string, timezone: string) => void;
  onCancel: () => void;
  /** When rescheduling, the existing schedule (UTC ISO) to pre-fill the form. */
  initialScheduledAt?: string | null;
}

/**
 * Campaigns are scheduled in the COMPANY timezone, matching how the rest of
 * the app schedules (appointments, mass-scheduler). All wall-clock↔UTC
 * conversion goes through the shared date-fns-tz helpers — NOT Intl /
 * toLocaleString, which eserv-web globally monkey-patches to the company
 * timezone (TimezoneContext), silently defeating any hand-rolled math.
 */
const ScheduleModal = ({
  isOpen,
  campaignName,
  recipientCount,
  isScheduling,
  onConfirm,
  onCancel,
  initialScheduledAt,
}: ScheduleModalProps) => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.schedule";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { timezone } = useAuthContext();
  const companyTz =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Today and any reschedule pre-fill are rendered in the company timezone
  // via date-fns-tz (survives the Intl monkey-patch).
  const minDate = formatInTimeZone(new Date(), companyTz, "yyyy-MM-dd");
  const seedDate = initialScheduledAt
    ? formatInTimeZone(new Date(initialScheduledAt), companyTz, "yyyy-MM-dd")
    : "";
  const seedTime = initialScheduledAt
    ? formatInTimeZone(new Date(initialScheduledAt), companyTz, "HH:mm")
    : "";

  const [date, setDate] = useState(seedDate);
  const [time, setTime] = useState(seedTime);

  if (!isOpen) {
    return null;
  }

  const handleConfirm = () => {
    if (!date || !time) {
      return;
    }
    // convertToUTC interprets the wall time in the company timezone
    // (date-fns-tz zonedTimeToUtc) and returns the UTC ISO instant.
    onConfirm(convertToUTC(date, time, companyTz), companyTz);
  };

  const isValid = date && time;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4">{tr("title")}</h2>

        <div className="space-y-4 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{tr("campaignName")}:</span>
            <span className="font-medium">{campaignName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{tr("recipients")}:</span>
            <span className="font-bold text-[#ff4f00]">
              {recipientCount ?? "--"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("date")}
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
              value={date}
              min={minDate}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("time")}
            </label>
            <input
              type="time"
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>

          <p className="text-xs text-gray-500">
            {tr("timezoneNote")} {companyTz}
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <OutlineButton
            size="small"
            onClick={onCancel}
            disabled={isScheduling}
          >
            {tr("cancel")}
          </OutlineButton>
          <Button
            primary
            size="small"
            onClick={handleConfirm}
            disabled={!isValid || isScheduling}
            isLoading={isScheduling}
          >
            {isScheduling ? tr("scheduling") : tr("confirmSchedule")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
